import './BlogPostInfo.scss';

import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import { useLocation } from 'react-router';
import { ButtonV2, Icon, Title } from '@travelwin/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClickEventData, useAnalytics } from '@/analytics';

import { Link } from '../Link';
import {
  GOOGLE_ICON,
  TRIPADVISOR_ICON,
  TRIPADVISOR_RATING_URL,
} from '../TrustedBy/TrustedBy';

const className = 'c-BlogInfo';

type BlogPostInfoProps = {
  contentTable: { id: string; title: string }[];
};

const BlogPostInfo = ({ contentTable }: BlogPostInfoProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isCopied, setIsCopied] = useState(false);
  const analyticsContext = useAnalytics();

  const trackSocialClicked = (socialNetwork: string) => {
    const trackingData: ClickEventData = {
      element_id: 'btn_social-platform',
      element_text: socialNetwork,
    };
    analyticsContext?.trackClickAction(trackingData);
  };
  const trackUrlShared = (url: string) => {
    const trackingData: ClickEventData = {
      element_id: 'btn_blog-shared',
      element_text: url,
    };
    analyticsContext?.trackClickAction(trackingData);
  };

  return (
    <div className={className}>
      <div className={`${className}__title`}>
        <Title tag={2} level={3}>
          {t('blog.in_this_post', 'In this post...')}
        </Title>
      </div>
      <div className={`${className}__container`}>
        <div id="toc" className={`${className}__list`}>
          <ul>
            {contentTable.map((section) => (
              <li key={section?.id}>
                <Link
                  to={{
                    hash: section?.id,
                    search: location.search,
                  }}
                >
                  {section?.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className={`${className}__reviewsSnsContainer`}>
        <div className={`${className}__reviews`}>
          <a
            href={TRIPADVISOR_RATING_URL}
            className={`${className}__review-link`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackSocialClicked('Tripadvisor');
            }}
          >
            <Icon name={TRIPADVISOR_ICON} />
          </a>
          <Icon name={GOOGLE_ICON} />
        </div>

        <div className={`${className}__sns`}>
          <a
            aria-label={t('blog.facebook', 'Facebook')}
            href={'https://www.facebook.com/simlocal/'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackSocialClicked('Facebook');
            }}
          >
            <Icon name="facebookColored" />
          </a>
          <a
            aria-label={t('blog.instagram', 'Instagram')}
            href={'https://www.instagram.com/simlocal/?hl=en-gb'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackSocialClicked('Instagram');
            }}
          >
            <Icon name="instagramColored" />
          </a>
          <a
            aria-label={t('blog.x', 'X')}
            href={'https://x.com/simlocal'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackSocialClicked('X');
            }}
          >
            <Icon name="xColored" />
          </a>
          <a
            aria-label={t('blog.linkedIn', 'LinkedIn')}
            href={'https://www.linkedin.com/company/sim-local'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackSocialClicked('Linkedin');
            }}
          >
            <Icon name="linkedinColored" />
          </a>
          <a
            aria-label={t('blog.youtube', 'YouTube')}
            href={'https://www.youtube.com/@simlocal_esim'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackSocialClicked('Youtube');
            }}
          >
            <Icon name="youtubeColored" />
          </a>
          <a
            aria-label={t('blog.tikTok', 'TikTok')}
            href={'https://www.tiktok.com/@simlocal_esim'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackSocialClicked('TikTok');
            }}
          >
            <Icon name="tikTokColored" />
          </a>
          <Popover open={isCopied}>
            <PopoverTrigger asChild>
              <ButtonV2
                icons={{ leading: <Icon name="shareIconColored" /> }}
                variant="link"
                onClick={() => {
                  const currentUrl = window.location.href;
                  navigator.clipboard.writeText(currentUrl).then(() => {
                    setIsCopied(true);
                    setTimeout(() => setIsCopied(false), 2000);
                  });
                  trackUrlShared(currentUrl);
                }}
              />
            </PopoverTrigger>
            <PopoverPortal>
              <PopoverContent
                side="bottom"
                align="center"
                className={`${className}__floatingCopied`}
              >
                {t('blog.url_copied', 'Link Copied!')}
              </PopoverContent>
            </PopoverPortal>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default BlogPostInfo;
